import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Pause,
  Play,
  Pencil,
  Receipt,
  History,
  ShoppingBag,
  IndianRupee,
  TicketPercent,
  Ticket,
} from "lucide-react";
import { Button, Dropdown, Select, Spin, Tabs } from "antd";
import { getDiscountCode } from "services/discountCodes.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import usePageTitle from "hooks/usePageTitle/usePageTitle";
import CodeStatusTag from "./_blocks/CodeStatusTag";
import CodeTiles from "./_blocks/CodeTiles";
import CodeFormModal from "./_blocks/CodeFormModal";
import RedemptionsTab from "./_blocks/RedemptionsTab";
import HistoryTab from "./_blocks/HistoryTab";
import useCodeActions from "./commons/useCodeActions";
import { getCodeMenuItems } from "./commons/discountCodes.columns";
import {
  CODE_STATUS,
  buildRunOptions,
  formatRupees,
  formatSummaryLine,
  formatUsesTile,
  getCodeActions,
  isPortalCode,
  isSwitchedOffByShoup,
} from "./commons/discountCodes.helpers";
import { CODES_COPY } from "./commons/discountCodes.copy";

const LIST_PATH = "/shoup-community/discount-codes";

function DiscountCodeDetail() {
  const { codeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runId, setRunId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Bumped after edits and actions so the tabs reload.
  const [version, setVersion] = useState(0);

  usePageTitle(detail?.code);

  const load = useCallback(
    async (selectedRunId, { showLoading = false } = {}) => {
      if (showLoading) setLoading(true);
      const { data, errors } = await getDiscountCode(codeId, {
        run_id: selectedRunId,
      });
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        navigate(LIST_PATH);
        return;
      }
      // The portal manages event codes only (D78 R11).
      if (data && !isPortalCode(data)) {
        navigate(LIST_PATH);
        return;
      }
      setDetail(data);
      setVersion((v) => v + 1);
    },
    [codeId, navigate]
  );

  useEffect(() => {
    setRunId(null);
    load(null, { showLoading: true });
  }, [load]);

  // After an action the latest run is shown (Run again starts a new one).
  const { runAction, contextHolder } = useCodeActions({
    onDone: () => {
      setRunId(null);
      load(null);
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  if (!detail) return null;

  const runs = detail.runs ?? [];
  const latestRunId = runs[0]?.run_id ?? detail.run_id;
  const isLatestRun = !runId || runId === latestRunId;
  // Older runs are read-only: actions always act on the latest run.
  const actions = isLatestRun
    ? getCodeActions(detail.status, detail.ended_reason)
    : { ...getCodeActions(CODE_STATUS.ENDED), canRunAgain: false };
  const isCancelled = detail.status === CODE_STATUS.CANCELLED;
  const activeTab =
    searchParams.get("tab") === "history" ? "history" : "redemptions";

  const handleRunChange = (value) => {
    setRunId(value);
    load(value);
  };

  const moreItems = getCodeMenuItems({
    row: detail,
    onAction: runAction,
    includePauseResume: false,
  }).filter((item) => {
    if (item.key === "run-again") return actions.canRunAgain;
    if (item.key === "end") return actions.canEnd;
    return true;
  });

  const tiles = [
    {
      key: "uses",
      label: CODES_COPY.tileUses,
      icon: ShoppingBag,
      colorClass: "bg-blue-100 text-blue-600",
      value: formatUsesTile(detail),
    },
    {
      key: "tickets",
      label: CODES_COPY.tileTicketsSold,
      icon: Ticket,
      colorClass: "bg-teal-100 text-teal-600",
      value: Number(detail.stats?.tickets_sold ?? 0).toLocaleString("en-IN"),
    },
    {
      key: "revenue",
      label: CODES_COPY.tileRevenueDetail,
      icon: IndianRupee,
      colorClass: "bg-emerald-100 text-emerald-600",
      value: formatRupees(detail.stats?.revenue),
    },
    {
      key: "discount",
      label: CODES_COPY.tileCodeDiscount,
      icon: TicketPercent,
      colorClass: "bg-amber-100 text-amber-600",
      value: formatRupees(detail.stats?.code_discount_given),
    },
  ];

  const tabItems = [
    {
      key: "redemptions",
      label: (
        <span className="flex items-center gap-2">
          <Receipt className="h-4 w-4" /> {CODES_COPY.tabRedemptions}
        </span>
      ),
      children: (
        <RedemptionsTab
          key={`r-${version}`}
          codeId={codeId}
          runId={runId ?? detail.run_id}
        />
      ),
    },
    {
      key: "history",
      label: (
        <span className="flex items-center gap-2">
          <History className="h-4 w-4" /> {CODES_COPY.tabHistory}
        </span>
      ),
      children: <HistoryTab key={`h-${version}`} codeId={codeId} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="text"
          icon={<ChevronLeft className="h-4 w-4" />}
          onClick={() => navigate(LIST_PATH)}
        >
          {CODES_COPY.detailBack}
        </Button>
        <div className="flex items-center space-x-2">
          {actions.canEdit && (
            <Button
              size="small"
              icon={<Pencil className="h-4 w-4" />}
              onClick={() => setEditOpen(true)}
            >
              {CODES_COPY.actionEdit}
            </Button>
          )}
          {actions.canPause && (
            <Button
              size="small"
              icon={<Pause className="h-4 w-4" />}
              onClick={() => runAction(detail, "PAUSE")}
            >
              {CODES_COPY.actionPause}
            </Button>
          )}
          {actions.canResume && (
            <Button
              size="small"
              type="primary"
              icon={<Play className="h-4 w-4" />}
              onClick={() => runAction(detail, "RESUME")}
            >
              {CODES_COPY.actionResume}
            </Button>
          )}
          <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
            <Button
              size="small"
              icon={<ChevronDown size={12} />}
              iconPosition="end"
            >
              {CODES_COPY.actionMore}
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Title block */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">{detail.code}</h1>
          <CodeStatusTag
            status={detail.status}
            endedReason={detail.ended_reason}
          />
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-muted-foreground text-sm">
            {formatSummaryLine(detail)}
          </p>
          {runs.length > 1 && (
            <Select
              size="small"
              value={runId ?? latestRunId}
              onChange={handleRunChange}
              options={buildRunOptions(runs)}
              style={{ minWidth: 200 }}
              aria-label="Run"
            />
          )}
        </div>
        {detail.private_note && (
          <p className="text-muted-foreground text-sm">
            Note: {detail.private_note}
          </p>
        )}
        {isCancelled && (
          <p className="text-muted-foreground text-sm">
            {isSwitchedOffByShoup(detail)
              ? CODES_COPY.switchedOffDetail
              : CODES_COPY.cancelledDetail}
          </p>
        )}
      </div>

      <CodeTiles tiles={tiles} loading={false} colProps={{ xs: 12, lg: 6 }} />

      {/* contain: the tab tables scroll inside the card on a phone (S-237) */}
      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card [contain:inline-size]">
        <div className="px-4 md:px-6">
          <Tabs
            activeKey={activeTab}
            onChange={(key) =>
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (key === "redemptions") next.delete("tab");
                else next.set("tab", key);
                return next;
              })
            }
            items={tabItems}
          />
        </div>
      </div>

      {contextHolder}

      <CodeFormModal
        open={editOpen}
        codeId={codeId}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setRunId(null);
          load(null);
        }}
      />
    </div>
  );
}

export default DiscountCodeDetail;
